import { IconInfoCircleFilled, IconPencil, IconX } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'reactstrap';
import { NSButton, NSInput } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { percentFormatter } from 'ns_libs/formatter';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { IFormattedCashFlowPeriod, IProFormaSourceTableRow } from 'views/ProFormaTable/types';
import PeriodCellValue from '../../../DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import SofrRefreshConfirmationModal from '../SofrRefreshConfirmationModal/SofrRefreshConfirmationModal';
import { renderSpacerCells, SOFR_HORIZON_TOOLTIP } from './helpers';

type SofrRateField = 'baseRate' | 'spread';

export interface ISofrRateRowsProps {
    source: IProFormaSourceTableRow;
    projectionPeriods: IFormattedCashFlowPeriod[];
    localStorageCadence: Periods;
}

const SofrRateRows = ({ source, projectionPeriods, localStorageCadence }: ISofrRateRowsProps) => {
    const { handleUpdateSource, handleRefreshSourceSofrRates } = useProFormaSocketContext();

    const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
    const [isEditingSpread, setIsEditingSpread] = useState(false);
    const [spreadDraft, setSpreadDraft] = useState('');
    const [showHorizonTooltip, setShowHorizonTooltip] = useState(false);

    const isMonthly = localStorageCadence === Periods.MONTHLY;
    const isYearly = localStorageCadence === Periods.ANNUALLY;

    const sofrByPeriodName = useMemo(() => {
        const map: Record<string, { index: number; baseRate: string; spread: string }> = {};

        source.sofrLoanRates?.periods.forEach((period, index) => {
            map[period.periodName] = { index, baseRate: period.baseRate, spread: period.spread };
        });

        return map;
    }, [source.sofrLoanRates?.periods]);

    const effectiveRateByPeriodName = useMemo(() => {
        const map: Record<string, string> = {};

        source.variableLoanRates?.variableLoanRates.forEach(cadence => {
            cadence.periods.forEach(period => {
                map[period.periodName] = period.loanRate;
            });
        });

        return map;
    }, [source.variableLoanRates?.variableLoanRates]);

    if (!source.sofrLoanRates) return null;

    const { defaultSpread } = source.sofrLoanRates;

    const updateSofrPeriod = (periodName: string, field: SofrRateField, updatedValue: string) => {
        const target = sofrByPeriodName[periodName];
        if (!source.sofrLoanRates || !target) return;

        const sanitizedValue = Math.max(Number(updatedValue), 0).toString();
        const updatedPeriods = [...source.sofrLoanRates.periods];
        updatedPeriods.splice(target.index, 1, { ...updatedPeriods[target.index], [field]: sanitizedValue });

        handleUpdateSource(source.id, {
            sofrLoanRates: { ...source.sofrLoanRates, periods: updatedPeriods },
        });
    };

    const applyDefaultSpread = () => {
        if (!source.sofrLoanRates) return;
        const sanitizedValue = Math.max(Number(spreadDraft) / 100, 0).toString();
        handleUpdateSource(source.id, {
            sofrLoanRates: { ...source.sofrLoanRates, defaultSpread: sanitizedValue },
        });
        setIsEditingSpread(false);
    };

    const startEditingSpread = () => {
        setSpreadDraft(String(Number(defaultSpread) * 100));
        setIsEditingSpread(true);
    };

    const confirmRefresh = () => {
        handleRefreshSourceSofrRates(source.id);
        setIsRefreshModalOpen(false);
    };

    const readOnlyRateValue = (field: SofrRateField, periodLabel: string, index: number): string => {
        if (isYearly) {
            const bucket = source.sofrYearlyRates?.[index];
            return bucket ? (percentFormatter(Number(bucket[field]) * 100) ?? '—') : '';
        }
        
        const sofrPeriod = sofrByPeriodName[periodLabel];
        return sofrPeriod ? (percentFormatter(Number(sofrPeriod[field]) * 100) ?? '—') : '';
    };

    const renderRateCells = (field: SofrRateField) => projectionPeriods.map((period, index) => {
        const sofrPeriod = sofrByPeriodName[period.label];
        const canEdit = isMonthly && !!sofrPeriod;

        return (
            <PeriodCellValue
                key={`sofr-${field}-${period.value}`}
                tooltipId={`sofr-${field}-${period.value}`}
                period={period}
                value={isMonthly ? '' : readOnlyRateValue(field, period.label, index)}
                cellClassName={classNames('NSTable__tbody__tr__td text-dark text-right', { 'pt-1': canEdit })}
                overlayingCellInput={canEdit ? (
                    <div className="d-flex align-items-center justify-content-end">
                        <NSCellInput
                            isRightAligned
                            type="number"
                            numberType="percentage"
                            value={sofrPeriod[field]}
                            handleUpdateValue={updatedValue => updateSofrPeriod(period.label, field, String(updatedValue))}
                            inputClassName="my-1"
                            placeholder="Enter %"
                            labelClassName="my-2"
                            inputHeight="33px"
                        />
                    </div>
                ) : null}
            />
        );
    });

    const effectiveRateValue = (periodLabel: string, index: number): string => {
        if (isYearly) {
            const bucket = source.sofrYearlyRates?.[index];
            return bucket ? (percentFormatter(Number(bucket.effectiveRate) * 100) ?? '—') : '';
        }
        const effectiveRate = effectiveRateByPeriodName[periodLabel];
        return effectiveRate !== undefined ? (percentFormatter(Number(effectiveRate) * 100) ?? '—') : '';
    };

    const renderEffectiveRateCells = () => projectionPeriods.map((period, index) => (
        <PeriodCellValue
            key={`sofr-effective-${period.value}`}
            tooltipId={`sofr-effective-${period.value}`}
            period={period}
            value={effectiveRateValue(period.label, index)}
            cellClassName="NSTable__tbody__tr__td text-dark text-right"
        />
    ));

    return (
        <>
            <tr className="NSTable__tbody__tr Source__row">
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle">
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="ml-1 text-dark">Term SOFR (1M)</span>
                        <div className="d-flex align-items-center">
                            <NSButton
                                text="Update"
                                size="sm"
                                color="primary"
                                outline
                                callback={() => setIsRefreshModalOpen(true)}
                            />
                            <NSIcon
                                id={`sofr-horizon-tooltip-${source.id}`}
                                icon={IconInfoCircleFilled}
                                className="my-auto ml-2 text-dark"
                            />
                            <Tooltip
                                target={`sofr-horizon-tooltip-${source.id}`}
                                isOpen={showHorizonTooltip}
                                toggle={() => setShowHorizonTooltip(!showHorizonTooltip)}
                            >
                                {SOFR_HORIZON_TOOLTIP}
                            </Tooltip>
                        </div>
                    </div>
                </td>
                {renderSpacerCells()}
                {renderRateCells('baseRate')}
            </tr>

            <tr className="NSTable__tbody__tr Source__row">
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle">
                    <div className="d-flex align-items-center justify-content-between">
                        <span className="ml-1 text-dark">+ Spread</span>
                        {isMonthly && isEditingSpread && (
                            <div className="d-flex align-items-center">
                                <NSInput
                                    name="sofrDefaultSpread"
                                    type="number"
                                    value={spreadDraft}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSpreadDraft(e.target.value)}
                                    inputWidth="60px"
                                    className="text-primary"
                                    placeholder="%"
                                />
                                <NSButton text="Apply" size="sm" color="primary" callback={applyDefaultSpread} className="ml-2" />
                                <NSButton
                                    icon={IconX}
                                    size="sm"
                                    className="px-1 ml-1 text-muted hover:text-dark bg-none hover:bg-light"
                                    ariaLabel="Cancel spread edit"
                                    callback={() => setIsEditingSpread(false)}
                                />
                            </div>
                        )}
                        {isMonthly && !isEditingSpread && (
                            <div className="d-flex align-items-center">
                                <span className="text-primary">{percentFormatter(Number(defaultSpread) * 100)}</span>
                                <NSButton
                                    icon={IconPencil}
                                    size="sm"
                                    className="px-2 ml-1 text-muted hover:text-dark bg-none hover:bg-light"
                                    callback={startEditingSpread}
                                />
                            </div>
                        )}
                    </div>
                </td>
                {renderSpacerCells()}
                {renderRateCells('spread')}
            </tr>

            <tr className="NSTable__tbody__tr Source__row">
                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle">
                    <span className="ml-1 text-dark">= Effective Rate</span>
                </td>
                {renderSpacerCells()}
                {renderEffectiveRateCells()}
            </tr>

            <SofrRefreshConfirmationModal
                isOpen={isRefreshModalOpen}
                toggle={() => setIsRefreshModalOpen(false)}
                onConfirm={confirmRefresh}
            />
        </>
    );
};

export default SofrRateRows;
